// Настройки → MCP: адрес сервера, ключи доступа (ключ показан один раз) и выданные OAuth-доступы.

import { useState, type FormEvent } from 'react'

import { ApiError, errorMessage } from '@/api/client'
import { Badge, Button, Card, EmptyState, ErrorText, Input, Modal, PageSpinner, useToast } from '@/components/ui'
import { formatDateTime } from '@/lib/format'

import {
  useCreateKey,
  useMcpInfo,
  useMcpKeys,
  useOAuthGrants,
  useRevokeGrant,
  useRevokeKey,
  type ApiKey,
  type CreatedKey,
  type OAuthGrant,
} from './api'
import { claudeCodeCommand, curlExample, keyState, lastUsedText, maskedKey } from './logic'

function useCopy() {
  const toast = useToast()
  return async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.show({ message: `${what} скопирован`, tone: 'accent' })
    } catch {
      toast.show({ message: 'Не удалось скопировать — выдели текст вручную', tone: 'danger' })
    }
  }
}

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-surface-2 px-3 py-2 font-mono text-xs text-fg">
      {children}
    </pre>
  )
}

function ConnectionCard({ url }: { url: string }) {
  const copy = useCopy()
  return (
    <Card
      title="Подключение"
      subtitle="MCP-сервер: инструменты чтения и записи. Каждая запись попадает в журнал и откатывается."
    >
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 break-all rounded-lg bg-surface-2 px-3 py-2 font-mono text-sm text-fg">{url}</code>
        <Button size="sm" icon="copy" onClick={() => void copy(url, 'Адрес')}>
          Копировать
        </Button>
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-muted">
        <li>
          <span className="text-fg">Claude.ai и ChatGPT</span> — добавь коннектор с этим адресом. Вход и согласие пройдут
          здесь, доступ появится в списке ниже.
        </li>
        <li>
          <span className="text-fg">Свой бот, n8n, Claude Code</span> — выпусти ключ и передавай его в заголовке{' '}
          <code className="font-mono">Authorization: Bearer …</code>
        </li>
      </ul>
    </Card>
  )
}

function SecretModal({ created, url, onClose }: { created: CreatedKey | null; url: string; onClose: () => void }) {
  const copy = useCopy()
  return (
    <Modal
      open={created !== null}
      onClose={onClose}
      title={created ? `Ключ «${created.key.name}»` : 'Ключ'}
      description="Ключ показан один раз — скопируй его сейчас. Потом его не увидеть: хранится только отпечаток."
      footer={
        <>
          <Button icon="copy" onClick={() => created && void copy(created.secret, 'Ключ')}>
            Копировать ключ
          </Button>
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        </>
      }
    >
      {created ? (
        <div className="grid gap-3">
          <Code>{created.secret}</Code>
          <div className="text-sm text-muted">Claude Code:</div>
          <Code>{claudeCodeCommand(url, created.secret)}</Code>
          <div className="text-sm text-muted">Проверка из терминала:</div>
          <Code>{curlExample(url, created.secret)}</Code>
        </div>
      ) : null}
    </Modal>
  )
}

function KeyRow({ apiKey, onRevoke }: { apiKey: ApiKey; onRevoke: (key: ApiKey) => void }) {
  const state = keyState(apiKey)
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-fg">{apiKey.name}</div>
        <div className="text-muted">
          <span className="font-mono">{maskedKey(apiKey.prefix)}</span> · выпущен {formatDateTime(apiKey.created_at)} ·{' '}
          {lastUsedText(apiKey.last_used_at)}
        </div>
      </div>
      <Badge tone={state.tone}>{state.text}</Badge>
      {apiKey.revoked_at ? null : (
        <Button size="sm" variant="ghost" onClick={() => onRevoke(apiKey)}>
          Отозвать
        </Button>
      )}
    </li>
  )
}

function KeysCard({ url }: { url: string }) {
  const keys = useMcpKeys()
  const create = useCreateKey()
  const revoke = useRevokeKey()
  const toast = useToast()
  const [name, setName] = useState('')
  const [created, setCreated] = useState<CreatedKey | null>(null)
  const [revoking, setRevoking] = useState<ApiKey | null>(null)

  const fieldError = create.error instanceof ApiError && create.error.field === 'name' ? create.error.message : null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    create.mutate(name, {
      onSuccess: (res) => {
        setCreated(res)
        setName('')
      },
      onError: (err) => {
        if (!(err instanceof ApiError && err.field === 'name')) toast.show({ message: errorMessage(err), tone: 'danger' })
      },
    })
  }

  return (
    <Card title="Ключи доступа" subtitle="Для своего бота, n8n, Claude Code и любых MCP-клиентов.">
      <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
        <Input
          label="Имя ключа"
          placeholder="telegram-bot"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          error={fieldError}
          containerClassName="min-w-48 flex-1"
        />
        <Button type="submit" variant="primary" icon="plus" loading={create.isPending} disabled={!name.trim()}>
          Выпустить ключ
        </Button>
      </form>

      <div className="mt-4">
        {keys.isPending ? (
          <PageSpinner />
        ) : keys.isError ? (
          <ErrorText error={keys.error} />
        ) : keys.data.length === 0 ? (
          <EmptyState icon="key" title="Ключей пока нет" description="Выпусти ключ — он нужен боту или n8n для доступа к MCP." />
        ) : (
          <ul className="divide-y divide-border">
            {keys.data.map((k) => (
              <KeyRow key={k.id} apiKey={k} onRevoke={setRevoking} />
            ))}
          </ul>
        )}
      </div>

      <SecretModal created={created} url={url} onClose={() => setCreated(null)} />
      <Modal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={revoking ? `Отозвать ключ «${revoking.name}»?` : 'Отозвать ключ?'}
        description="Клиент с этим ключом сразу потеряет доступ. Отменить нельзя — понадобится новый ключ."
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>Отмена</Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() =>
                revoking &&
                revoke.mutate(revoking.id, {
                  onSuccess: () => {
                    toast.show({ message: 'Ключ отозван', tone: 'accent' })
                    setRevoking(null)
                  },
                  onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
                })
              }
            >
              Отозвать
            </Button>
          </>
        }
      />
    </Card>
  )
}

function GrantsCard() {
  const grants = useOAuthGrants()
  const revoke = useRevokeGrant()
  const toast = useToast()
  const [revoking, setRevoking] = useState<OAuthGrant | null>(null)

  return (
    <Card title="Выданные доступы" subtitle="Коннекторы, подключённые через вход и согласие (OAuth).">
      {grants.isPending ? (
        <PageSpinner />
      ) : grants.isError ? (
        <ErrorText error={grants.error} />
      ) : grants.data.length === 0 ? (
        <EmptyState icon="shield" title="Доступов нет" description="Появятся после подключения коннектора Claude.ai или ChatGPT." />
      ) : (
        <ul className="divide-y divide-border">
          {grants.data.map((g) => (
            <li key={g.client_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-fg">{g.client_name}</div>
                <div className="text-muted">
                  разрешён {formatDateTime(g.granted_at)} · {lastUsedText(g.last_used_at)}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setRevoking(g)}>
                Отозвать
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Modal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={revoking ? `Отозвать доступ «${revoking.client_name}»?` : 'Отозвать доступ?'}
        description="Все токены клиента перестанут работать. Чтобы вернуть доступ, подключи коннектор заново."
        footer={
          <>
            <Button onClick={() => setRevoking(null)}>Отмена</Button>
            <Button
              variant="danger"
              loading={revoke.isPending}
              onClick={() =>
                revoking &&
                revoke.mutate(revoking.client_id, {
                  onSuccess: () => {
                    toast.show({ message: 'Доступ отозван', tone: 'accent' })
                    setRevoking(null)
                  },
                  onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
                })
              }
            >
              Отозвать
            </Button>
          </>
        }
      />
    </Card>
  )
}

export function McpSettingsPage() {
  const info = useMcpInfo()
  if (info.isPending) return <PageSpinner />
  if (info.isError) return <ErrorText error={info.error} />
  return (
    <div className="grid max-w-3xl gap-4">
      <ConnectionCard url={info.data.mcp_url} />
      <KeysCard url={info.data.mcp_url} />
      <GrantsCard />
    </div>
  )
}
